import { ApiClient } from './api-client';
import type {
  AdminRegion,
  Category,
  City,
  Country,
  EventSpecialization,
  ServiceCatalog,
} from '../types';

export interface CitySearchQuery {
  /** ISO-2 alpha-2 code, e.g. 'DE'. Server uppercases regardless. */
  country?: string;
  adminRegionId?: string;
  /** Prefix or substring match against the city name / slug. */
  q?: string;
}

export const citiesService = {
  list: async (params: CitySearchQuery = {}): Promise<City[]> =>
    (await ApiClient.get<City[], CitySearchQuery>('/cities', params)).data,

  /** ISO-2 country code the platform should default the UI to. */
  defaultCountry: async (): Promise<string> => {
    const res = await ApiClient.get<{ defaultCountry: string }>('/cities/default-country');
    return res.data.defaultCountry;
  },
};

/**
 * Countries the platform is actively serving. The backend may still know
 * about more (its `countries` table is global), but the UI hides them so
 * users can't pick a market we haven't onboarded yet — every country
 * picker (top-bar dialog, category page, signup, etc.) consumes this
 * whitelist via {@link countriesService.list}. Add an alpha-2 here when
 * a new market goes live.
 */
const ENABLED_COUNTRIES: ReadonlySet<string> = new Set(['DE', 'FR', 'BE', 'SN']);

export const countriesService = {
  /**
   * List the platform's active countries, with display names localised
   * to `locale` (defaults to the user's current i18n language at the call
   * site).  Filtered to {@link ENABLED_COUNTRIES} so users only see the
   * markets we're currently serving.
   */
  list: async (locale?: string): Promise<Country[]> => {
    const all = (await ApiClient.get<Country[], { locale?: string }>(
      '/countries',
      locale ? { locale } : {},
    )).data;
    return all.filter((c) => ENABLED_COUNTRIES.has(c.alpha2));
  },
};

export const adminRegionsService = {
  /**
   * Subdivisions of a country (Bundesländer / régions / provinces).
   * Returns an empty list when no country is given so the UI can render
   * "select a country first" without an extra branch.
   */
  list: async (countryCode: string, locale?: string): Promise<AdminRegion[]> => {
    if (!countryCode) return [];
    const res = await ApiClient.get<AdminRegion[], { countryCode: string; locale?: string }>(
      '/admin-regions',
      locale ? { countryCode, locale } : { countryCode },
    );
    return res.data;
  },
};

export const categoriesService = {
  /**
   * Active service categories for a market. Pass the visitor's selected
   * country (ISO-2) so markets with their own taxonomy — e.g. Senegal —
   * get their country-specific categories; every other market (and an
   * omitted code) gets the global launch set, unchanged.
   */
  list: async (country?: string): Promise<Category[]> =>
    (await ApiClient.get<Category[], { country?: string }>(
      '/categories',
      country ? { country } : {},
    )).data,

  servicesIn: async (categoryId: string): Promise<ServiceCatalog[]> =>
    (await ApiClient.get<ServiceCatalog[]>(`/categories/${categoryId}/services`)).data,
};

export const eventSpecializationsService = {
  list: async (): Promise<EventSpecialization[]> =>
    (await ApiClient.get<EventSpecialization[]>('/event-specializations')).data,
};
